import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';

import { QRCodeSection, OTPInput, StatusIndicator, getStatusDisplay } from '@/components/kiosk';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const KioskDisplay = () => {
  const { kioskId } = useParams();
  const [kiosk, setKiosk] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);
  const [otpInput, setOtpInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobQueue, setJobQueue] = useState([]);
  
  // Use ref for interval to avoid dependency issues
  const intervalRef = useRef(null);

  const fetchKioskData = useCallback(async () => {
    try {
      const [kioskRes, jobsRes] = await Promise.all([
        axios.get(`${API}/kiosks/${kioskId}`),
        axios.get(`${API}/print-jobs?kiosk_id=${kioskId}&status=paid`)
      ]);

      setKiosk(kioskRes.data);
      setJobQueue(jobsRes.data);

      setCurrentJob((prev) => {
        if (jobsRes.data.length > 0 && !prev) {
          return jobsRes.data[0];
        }
        return prev;
      });
    } catch (error) {
      console.error('Error fetching kiosk data:', error);
    }
  }, [kioskId]);

  useEffect(() => {
    fetchKioskData();
    intervalRef.current = setInterval(fetchKioskData, 5000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchKioskData]);

  const handleOtpSubmit = async () => {
    if (!currentJob || otpInput.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/print/trigger`, {
        job_id: currentJob.id,
        otp: otpInput
      });
      
      toast.success('Print job started!');
      setOtpInput('');
      setCurrentJob(null);
      fetchKioskData();
    } catch (error) {
      console.error('OTP verification error:', error);
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const status = getStatusDisplay(jobQueue, currentJob);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-primary/90 text-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src="/inchpaper-printezy.png" 
            alt="Inchpaper" 
            className="h-16 sm:h-20 mx-auto mb-4 bg-white/95 px-4 py-2 rounded-xl object-contain shadow-md"
            onError={(e) => { e.target.src = '/inchpaper-logo.png'; }}
          />
          {kiosk && (
            <div className="space-y-1">
              <p className="text-2xl font-medium">{kiosk.name}</p>
              <p className="text-lg text-white/70">{kiosk.location}</p>
            </div>
          )}
        </div>

        {/* Main Status Card */}
        <Card className="max-w-2xl mx-auto bg-white text-foreground mb-8" data-testid="status-card">
          <CardContent className="p-12">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                {status.icon}
              </div>
              <div>
                <h2 className="font-heading text-4xl font-bold mb-2">
                  {status.title}
                </h2>
                <p className="text-xl text-muted-foreground">
                  {status.description}
                </p>
              </div>

              <OTPInput
                currentJob={currentJob}
                otpInput={otpInput}
                setOtpInput={setOtpInput}
                onSubmit={handleOtpSubmit}
                loading={loading}
              />

              {/* Queue Info */}
              {jobQueue.length > 1 && (
                <div className="pt-6 border-t border-border">
                  <p className="text-muted-foreground">
                    {jobQueue.length - 1} job{jobQueue.length - 1 > 1 ? 's' : ''} in queue
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* QR Code Section */}
        {jobQueue.length === 0 && (
          <QRCodeSection kiosk={kiosk} kioskId={kioskId} />
        )}

        <StatusIndicator kiosk={kiosk} />
      </div>
    </div>
  );
};

export default KioskDisplay;
